import { Card, Form, Typography, Button, Statistic, Col, InputNumber, Select, Descriptions, Tag } from 'antd';
import { users } from '../../../../../../../API/TT_ELD/Users/user';
import { companies } from '../../../../../../../API/TT_ELD/Users/company';
import { useParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useCompanyData } from '../../../../../../../Hooks/TT_ELD/Companies';
import { getUserFromLocalStorage } from '../../../../../../../Utils/sidebar';
import moment from 'moment';

const { Countdown } = Statistic;
const { Text } = Typography;

type MyObjectType = { [key: string | number]: any };
type params = { readonly id: any };
interface FreeTrialResponse { data: any; error: string }

const CAN_SUBMIT_ROLES = ['support_manager', 'manager', 'sales'];
const EXTENDED_LIMIT_ROLES = ['manager', 'sales'];

const FreeTrial = () => {
  const [form] = Form.useForm();
  const { id } = useParams<params>();
  const { data, isFetching, isLoading, refetch }: MyObjectType = useCompanyData(id);
  const currentRole = getUserFromLocalStorage()?.role ?? '';
  const canSubmit = CAN_SUBMIT_ROLES.includes(currentRole);
  const maxLimitDays = EXTENDED_LIMIT_ROLES.includes(currentRole) ? 30 : 10;

  const freeTrial = data?.freeTrial;
  const isFreeTrialActive = freeTrial?.status === 'active';
  const freeTrialEndDate = freeTrial?.endDate ? new Date(freeTrial.endDate) : undefined;
  const isActivated = !!freeTrial;

  const onPost = useMutation(
    'freeTrial',
    ({ values, id }: { values: string | number; id: string | number }) =>
      users.freeTrial(values, id),
    { onSuccess: () => { refetch(); } },
  );

  const onPatch = useMutation(
    ({ freeTrialId, quantity }: { freeTrialId: number | string; quantity: number }) =>
      companies.updateFreeTrial(freeTrialId, quantity),
    { onSuccess: () => { refetch(); } },
  );

  const onFinish = async (values: any) => {
    if (freeTrial?.id) {
      await onPatch.mutate({ freeTrialId: id, quantity: values.quantity });
    } else {
      await onPost.mutate({ values, id });
    }
  };

  const isSaving = onPost.isLoading || onPatch.isLoading;

  type FieldType = { limit?: number; quantity?: string };

  return (
    <div className="mainPage">
      <Card loading={isLoading || isFetching} title="Free trial" style={{ width: '100%' }}>

        {/* ── Countdown ── */}
        {isFreeTrialActive && freeTrialEndDate && (
          <Col style={{ marginBottom: 32 }}>
            <Countdown
              valueStyle={{ fontSize: '32px' }}
              title="Free trial ends"
              value={freeTrialEndDate.getTime()}
              format=" DD : HH : mm : ss "
            />
            <Text type="secondary" style={{ margin: '0 22px 0 6px' }}>Days</Text>
            <Text type="secondary" style={{ margin: '0 10px 0 2px' }}>Hours</Text>
            <Text type="secondary" style={{ margin: '0 5px 0 5px' }}>Minutes</Text>
            <Text type="secondary" style={{ margin: '0 5px 0 5px' }}>Seconds</Text>
          </Col>
        )}

        {/* ── Free trial info ── */}
        {freeTrial && (
          <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
            <Descriptions.Item label="Status">
              <Tag color={freeTrial.status === 'active' ? 'green' : 'default'}>
                {freeTrial.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Outcome">
              <Tag color={
                freeTrial.outcome === 'converted' ? 'blue'
                : freeTrial.outcome === 'cancelled' ? 'red'
                : 'orange'
              }>
                {freeTrial.outcome ?? '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Days">{freeTrial.days ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Quantity">{freeTrial.quantity ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Start Date">
              {freeTrial.startDate ? moment(freeTrial.startDate).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="End Date">
              {freeTrial.endDate ? moment(freeTrial.endDate).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}

        {/* ── Form ── */}
        <Form
          form={form}
          layout="vertical"
          style={{ maxWidth: 600 }}
          onFinish={onFinish}
          name="basic"
          initialValues={{
            limit: 7,
            quantity: freeTrial?.quantity ?? data?.quantity,
          }}
        >
          {!isActivated && (
            <Form.Item<FieldType>
              label="Limit"
              name="limit"
              rules={[{ required: true, message: 'Please input your limit!' }]}
            >
              <Select style={{ width: '100%' }}>
                {Array.from({ length: maxLimitDays }, (_, i) => (
                  <Select.Option key={i + 1} value={i + 1}>
                    {i + 1} day{i + 1 > 1 ? 's' : ''}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item<FieldType>
            label="Quantity"
            name="quantity"
            rules={[{ required: true, message: 'Please input your quantity!' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          {canSubmit && (
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isSaving}>
                Submit
              </Button>
            </Form.Item>
          )}
        </Form>
      </Card>
    </div>
  );
};

export default FreeTrial;
